import axios from 'axios';
import { API_URL } from '../utils/apiConfig';

// 创建axios实例
const API = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// 请求拦截器，添加认证token
API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器，处理错误
API.interceptors.response.use(
  (response) => response,
  (error) => {
    // 401错误时清除token并重定向到登录页
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// 认证API
export const AuthAPI = {
  register: (userData) => API.post('/auth/register', userData),
  login: (credentials) => API.post('/auth/login', credentials),
  getCurrentUser: () => API.get('/auth/me'),
  updateLanguage: (language) => API.put('/auth/language', { language }),
  updateProfile: (profileData) => API.put('/auth/profile', profileData),
  changePassword: (passwordData) => API.put('/auth/password', passwordData),
  sendVerificationCode: (type) => API.post('/auth/send-code', { type }),
  forgotPassword: (email) => API.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => API.post('/auth/reset-password', { token, password }),
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append('avatar', file);
    return API.post('/upload/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  verifyInvitation: (code) => API.post('/auth/verify-invitation', { code }),
  createAuthRequest: (data) => API.post('/auth/auth-request', data),
  getAuthRequests: () => API.get('/auth/auth-requests'),
  getAuthRequestDetails: (code) => API.get(`/auth/auth-request/${code}`),
  deleteAccount: (code) => API.delete('/auth/account', { data: { code } })
};

// 设备API
export const DeviceAPI = {
  getDevices: () => API.get('/devices'),
  getDeviceTypes: () => API.get('/devices/types'),
  getDevice: (id) => {
    if (!id) {
      console.error('DeviceAPI.getDevice 收到无效的ID:', id);
      return Promise.reject(new Error('无效的设备ID'));
    }
    console.log('发送设备详情请求:', `/devices/${id}`);
    return API.get(`/devices/${id}`);
  },
  addDevice: (deviceData) => API.post('/devices', deviceData),
  getDeviceSettingsConfig: (id) => API.get(`/devices/${id}/settings`),
  getDeviceSettings: (id, packIndex) => API.get(`/devices/${id}/settings`, { params: { packIndex } }),
  updateDevice: (id, data) => API.put(`/devices/${id}`, data),
  sendDeleteCode: (id) => API.post(`/devices/${id}/send-delete-code`),
  deleteDevice: (id, code) => API.delete(`/devices/${id}`, { data: { code } }),

  getDeviceData: (id, params) => API.get(`/devices/${id}/data`, { params }),
  getLatestData: (id, params) => API.get(`/devices/${id}/latest`, { params }),
  getDeviceTypeStats: () => API.get('/devices/type-stats'),
  sendMqttCommand: (id, commandData) => API.post(`/devices/${id}/mqtt-command`, commandData),
  shareDevice: (id, data) => API.post(`/devices/${id}/share`, data),
  getSharedUsers: (id) => API.get(`/devices/${id}/shared-users`),
  removeSharedUser: (id, userId) => API.delete(`/devices/${id}/shared-users/${userId}`),
  getSettingsHistory: (id) => API.get(`/devices/${id}/history/settings`),
  getDeviceWarnings: (id, params) => API.get(`/devices/${id}/history/warnings`, { params })
};

// 管理员API
export const AdminAPI = {
  getUsers: () => API.get('/admin/users'),
  getUser: (id) => API.get(`/admin/users/${id}`),
  getUserDevices: (id) => API.get(`/admin/users/${id}/devices`),
  getDevices: () => API.get('/admin/devices'),
  getStats: () => API.get('/admin/stats'),
  createInvitation: (data) => API.post('/admin/invitations', data),
  getInvitations: () => API.get('/admin/invitations')
};

// 仪表盘API
export const DashboardAPI = {
  // 获取系统状态统计数据
  getStats: () => API.get('/dashboard/stats'),

  // 获取电池历史数据
  // 参数: 
  // - period: 预设时间段 '1h'/'24h'/'3d'/'7d'/'30d'
  // - deviceId: 设备ID
  // - metric: 查询指标，默认'all'
  // - from: 自定义开始日期（ISO字符串）
  // - to: 自定义结束日期（ISO字符串）
  getHistoryData: (params) => {
    console.log('获取历史数据:', params);
    return API.get('/dashboard/history', { params });
  }
};

// 添加新的数据查询API服务
export const DataAPI = {
  // 查询设备数据 - 使用 /devices/:deviceId/data 端点
  queryDeviceData: (params) => {
    const { deviceId, ...otherParams } = params;
    if (!deviceId) {
      console.error('DataAPI.queryDeviceData requires deviceId');
      return Promise.reject(new Error('Device ID is required'));
    }
    return API.get(`/devices/${deviceId}/data`, { params: otherParams });
  },

  // 获取设备最新数据
  getLatestData: (params) => {
    const { deviceId, ...otherParams } = params;
    if (!deviceId) {
      console.error('DataAPI.getLatestData requires deviceId');
      return Promise.reject(new Error('Device ID is required'));
    }
    return API.get(`/devices/${deviceId}/data`, { params: { ...otherParams, limit: 1 } });
  }
};

// API Key Management
export const ApiKeysAPI = {
  getKeys: () => API.get('/apikeys'),
  createKey: (name) => API.post('/apikeys', { name }),
  deleteKey: (id) => API.delete(`/apikeys/${id}`)
};

// 图库API
export const GalleryAPI = {
  // 获取公开分享的图片
  getSharedImages: (params) => API.get('/gallery', { params }),

  // 获取特定设备的图片
  getDeviceImages: (deviceId) => API.get(`/gallery/${deviceId}`),

  // 上传图片
  uploadImages: (deviceId, files, caption, shareToGallery = true) => {
    const formData = new FormData();
    files.forEach(file => formData.append('images', file));
    if (caption) formData.append('caption', caption);
    formData.append('shareToGallery', shareToGallery.toString());
    return API.post(`/gallery/${deviceId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // 切换分享状态
  toggleShare: (imageId, isShared) => API.patch(`/gallery/${imageId}/share`, { isShared }),

  // 点赞/取消点赞
  toggleLike: (imageId) => API.post(`/gallery/${imageId}/like`),

  // 删除图片
  deleteImage: (imageId) => API.delete(`/gallery/${imageId}`),

  // 管理员审核图片
  approveImage: (imageId, isApproved) => API.patch(`/gallery/${imageId}/approve`, { isApproved })
};

// 客户支持API
export const SupportAPI = {
  // Tags
  getTags: (type) => API.get('/support/tags', { params: { type } }),
  createTag: (data) => API.post('/support/tags', data),
  updateTag: (id, data) => API.patch(`/support/tags/${id}`, data),
  deleteTag: (id) => API.delete(`/support/tags/${id}`),

  // Posts
  getPosts: (params) => API.get('/support/posts', { params }),
  searchPosts: (query, limit) => API.get('/support/posts/search', { params: { q: query, limit } }),
  getSimilarPosts: (title, excludeId) => API.get('/support/posts/similar', { params: { title, excludeId } }),
  getPost: (id) => API.get(`/support/posts/${id}`),
  createPost: (data) => API.post('/support/posts', data),
  updatePost: (id, data) => API.patch(`/support/posts/${id}`, data),
  deletePost: (id) => API.delete(`/support/posts/${id}`),
  pinPost: (id, isPinned) => API.patch(`/support/posts/${id}/pin`, { isPinned }),
  lockPost: (id, isLocked) => API.patch(`/support/posts/${id}/lock`, { isLocked }),
  likePost: (id) => API.post(`/support/posts/${id}/like`),
  reportPost: (id, reason) => API.post(`/support/posts/${id}/report`, { reason }),

  // Replies
  createReply: (postId, content, attachmentIds = []) => API.post(`/support/posts/${postId}/replies`, { content, attachmentIds }),
  updateReply: (id, content) => API.patch(`/support/replies/${id}`, { content }),
  deleteReply: (id) => API.delete(`/support/replies/${id}`),
  likeReply: (id) => API.post(`/support/replies/${id}/like`),
  reportReply: (id, reason) => API.post(`/support/replies/${id}/report`, { reason }),

  // Attachments
  uploadAttachments: (files, postId, replyId) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    if (postId) formData.append('postId', postId);
    if (replyId) formData.append('replyId', replyId);
    return API.post('/support/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Drafts
  getDraft: () => API.get('/support/drafts'),
  saveDraft: (data) => API.put('/support/drafts', data),
  deleteDraft: () => API.delete('/support/drafts'),

  // Templates (Admin)
  getTemplates: () => API.get('/support/templates'),
  createTemplate: (data) => API.post('/support/templates', data),
  updateTemplate: (id, data) => API.patch(`/support/templates/${id}`, data),
  deleteTemplate: (id) => API.delete(`/support/templates/${id}`),

  // Reports (Admin)
  getReports: (status) => API.get('/support/reports', { params: { status } }),
  updateReport: (id, status) => API.patch(`/support/reports/${id}`, { status }),

  // Users (for @mention)
  // Users (for @mention)
  searchUsers: (query, limit) => API.get('/support/users/search', { params: { q: query, limit } })
};

// 新闻API
export const NewsAPI = {
  getPublicNews: (params) => API.get('/news/public', { params }),
  getAllNews: (params) => API.get('/news', { params }),
  getNews: (id) => API.get(`/news/public/${id}`),
  createNews: (data) => API.post('/news', data),
  updateNews: (id, data) => API.put(`/news/${id}`, data),
  deleteNews: (id) => API.delete(`/news/${id}`)
};

export default API;